import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import AdminAnalytics from './pages/AdminAnalytics';
import DeveloperPortal from './pages/DeveloperPortal';
import Widget from './pages/Widget';
import WidgetEmbed from './pages/WidgetEmbed';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Contact from './pages/Contact';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import SharedVehicle from './pages/SharedVehicle';
import ManageReminders from './pages/ManageReminders';
import UnsubscribePage from './pages/UnsubscribePage';
import ReminderActionPage from './pages/ReminderActionPage';
import MyAccountPage from './pages/MyAccountPage';
import FullReportPage from './pages/FullReportPage';
import BlogPage from './pages/BlogPage';
import BlogArticle from './pages/BlogArticle';
import NotFound from './pages/NotFound';
import SignInRequired from './pages/SignInRequired';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import './App.css';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  // Track page views
  useEffect(() => {
    const trackPageView = async () => {
      try {
        const sessionId = localStorage.getItem('chat_session_id') || 
                         sessionStorage.getItem('visitor_session_id') ||
                         'visitor_' + Date.now();
        
        if (!sessionStorage.getItem('visitor_session_id')) {
          sessionStorage.setItem('visitor_session_id', sessionId);
        }
        
        await axios.post(`${API}/track-visit`, null, {
          params: {
            page_path: location.pathname,
            session_id: sessionId
          }
        });
      } catch (error) {
        // Silently fail - don't interrupt user experience
        console.debug('Page view tracking failed:', error);
      }
    };
    
    trackPageView();
  }, [location.pathname]);

  // Check for existing session or process session_id from URL
  useEffect(() => {
    const checkAuth = async () => {
      // Check if session_id in URL fragment
      const hash = window.location.hash;
      console.log('🔍 Checking auth, URL hash:', hash);
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        console.log('✅ Found session_id in URL:', sessionId);
        
        try {
          // Exchange session_id for user data
          console.log('📡 Calling /auth/session endpoint...');
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          }, {
            withCredentials: true
          });
          
          console.log('✅ Auth successful, user:', response.data.user);
          setUser(response.data.user);
          
          // Clean URL hash (OAuth service already redirected to /dashboard)
          window.history.replaceState({}, document.title, window.location.pathname);
          console.log('✅ User authenticated, staying on current route');
        } catch (error) {
          console.error('❌ Auth error:', error.response?.data || error.message);
          // On auth error, redirect to landing
          navigate('/', { replace: true });
        }
        
        setLoading(false);
        setAuthChecked(true);
        return;
      }
      
      console.log('🔍 No session_id in URL, checking existing session...');
      
      // Check existing session
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        console.log('✅ Found existing session, user:', response.data);
        setUser(response.data);
      } catch (error) {
        console.log('ℹ️ No existing session');
        // Not authenticated
        setUser(null);
      }
      
      setLoading(false);
      setAuthChecked(true);
    };
    
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading || !authChecked) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route 
          path="/" 
          element={<LandingPage user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/dashboard" 
          element={
            user ? 
            <Dashboard user={user} setUser={setUser} onLogout={handleLogout} /> : 
            <SignInRequired />
          } 
        />
        <Route 
          path="/admin" 
          element={
            user ? 
            <AdminAnalytics user={user} onLogout={handleLogout} /> : 
            <SignInRequired title="Admin sign-in required" />
          } 
        />
        <Route 
          path="/developer" 
          element={
            user ? 
            <DeveloperPortal user={user} onLogout={handleLogout} /> : 
            <SignInRequired />
          } 
        />
        <Route 
          path="/widget" 
          element={<Widget />} 
        />
        <Route 
          path="/widget-embed" 
          element={<WidgetEmbed />} 
        />
        <Route 
          path="/privacy" 
          element={<PrivacyPolicy />} 
        />
        <Route 
          path="/terms" 
          element={<TermsOfService />} 
        />
        <Route 
          path="/contact" 
          element={<Contact />} 
        />
        <Route 
          path="/verify-email" 
          element={<VerifyEmail />} 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPassword />} 
        />
        <Route 
          path="/shared/:token" 
          element={<SharedVehicle />} 
        />
        <Route 
          path="/reminders" 
          element={user ? <ManageReminders /> : <SignInRequired />} 
        />
        <Route 
          path="/unsubscribe" 
          element={<UnsubscribePage />} 
        />
        <Route 
          path="/reminder-action" 
          element={<ReminderActionPage />} 
        />
        <Route 
          path="/account" 
          element={user ? <MyAccountPage user={user} onLogout={handleLogout} /> : <SignInRequired />} 
        />
        <Route 
          path="/report/:reportId" 
          element={user ? <FullReportPage user={user} /> : <SignInRequired title="Sign in to view report" />} 
        />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster position="top-center" />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;